import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { LedgerEntryLine } from '../clients/rust-api-client';
import AppSidebar from '../AppSidebar';
import { Navbar } from '../Components/Catalyst/navbar';
import { SidebarLayout } from '../Components/Catalyst/sidebar-layout';
import { Heading } from '../Components/Catalyst/heading';
import { Text } from '../Components/Catalyst/text';
import HomeSelector from '../Components/Custom/HomeSelector';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import LoadingSpinner from '../LoadingSpinner';
import { useMember } from '../Hooks/MemberContext';
import ActivityFeed from './ActivityFeed';
import EntryDetailDialog from './EntryDetailDialog';
import { ActivityFilter } from './entryLabels';

interface Props {
  currentPage: string;
}

const FILTERS: Array<{ value: ActivityFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'charges', label: 'Charges' },
  { value: 'payments', label: 'Payments' },
];

/**
 * The full account history.
 *
 * **Three filters, not sixteen.** front-porch offers a chip per ledger
 * entry type, which is the right tool for a manager reconciling a
 * specific kind of charge across an account. Nobody has ever wanted to
 * see only their `charge_adjustment` rows. Charges and payments are the
 * two questions a homeowner actually asks.
 */
const ActivityPage: React.FC<Props> = ({ currentPage }) => {
  const { homes, selectedHome, loading } = useMember();
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const [selected, setSelected] = useState<LedgerEntryLine | null>(null);
  const [error, setError] = useState<string | null>(null);

  const homeId = selectedHome?.home_id;

  if (loading) return <LoadingSpinner />;

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage={currentPage} />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <Link
        to="/account"
        className="text-sm text-sky-700 hover:underline dark:text-sky-400"
      >
        &larr; Back to your account
      </Link>

      <Heading className="mt-2">Account activity</Heading>

      {homes.length === 0 || !homeId ? (
        <Text className="mt-2">
          Your account is not linked to a home yet. Please contact your
          association manager.
        </Text>
      ) : (
        <>
          <div className="mt-2">
            <HomeSelector />
          </div>

          <ErrorAlert className="mt-4" onDismiss={() => setError(null)}>
            {error}
          </ErrorAlert>

          <div
            className="mt-5 inline-flex rounded-lg border border-zinc-300 p-0.5 dark:border-zinc-700"
            role="group"
            aria-label="Filter activity"
          >
            {FILTERS.map((option) => (
              <button
                key={option.value}
                type="button"
                aria-pressed={filter === option.value}
                onClick={() => setFilter(option.value)}
                className={
                  filter === option.value
                    ? 'rounded-md bg-sky-700 px-4 py-1.5 text-sm font-medium text-white'
                    : 'rounded-md px-4 py-1.5 text-sm text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-gray-700'
                }
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="mt-5">
            <ActivityFeed
              homeId={homeId}
              filter={filter}
              onSelect={setSelected}
              onError={setError}
            />
          </div>
        </>
      )}

      <EntryDetailDialog entry={selected} onClose={() => setSelected(null)} />
    </SidebarLayout>
  );
};

export default ActivityPage;
